/**
 * 🚀 HERO CONTENT ELEMENT 2025
 * 
 * Wrapper für jedes Element im HeroHeader
 * - Rendert Text oder Button Elemente
 * - Ghost-Elemente mit Plus erscheinen beim Hover
 * - Zeigt ElementControls (Delete, Reorder) im Edit Mode
 * - Minimalistisch und user-freundlich (Notion-Style)
 */

import React, { useState } from 'react';
import UniversalTextElement from './UniversalTextElement';
import UniversalButtonElement from './UniversalButtonElement';
import UniversalDividerElement from './UniversalDividerElement';
import UniversalImageElement from './UniversalImageElement';
import UniversalVideoElement from './UniversalVideoElement';
import UniversalGalleryElement from './UniversalGalleryElement';
import UniversalQAElement from './UniversalQAElement';
import UniversalCountdownBar from './UniversalCountdownBar';
import UniversalFooterElement from './UniversalFooterElement';
import LayoutGridElement from './LayoutGridElement';
import AddElementButton from './AddElementButton';
import ElementControls from './ElementControls';
import { gridPresets, applyGridPreset } from './gridPresets';
import './HeroContentElement.css';

function HeroContentElement({
    element,
    index,
    totalElements,
    isEditMode,
    isMobileLayout,
    onUpdate,
    onDelete,
    onDuplicate, // ✅ NEW: Duplicate Element Callback
    onMoveUp,
    onMoveDown,
    canMoveUp = false,
    canMoveDown = false,
    onAddBefore,
    onAddAfter,
    isSelected,
    onSelect,
    colors = {},
    colorAssignments = {},
    fonts = {},
    availableFonts = [],
    layoutConfig = {},
    textAlignment = 'left',
    currentlyEditingElementId = null,
    onEditingChange = () => {},
    // ✅ NEW: Flag to disable ghost buttons in grid cells
    disableGhostButtons = false,
    // ✅ NEW: Flag ob Elemente horizontal (row) oder vertikal (column) angeordnet sind
    isHorizontalLayout = false,
    // ✅ NEW: Nesting depth (0 = top level, 1 = inside grid, 2 = inside grid in grid, etc.)
    depth = 0
}) {
    const [showGhostTop, setShowGhostTop] = useState(false);
    const [showGhostBottom, setShowGhostBottom] = useState(false);
    const hideTimeoutRef = React.useRef(null);
    
    const handleMouseEnter = () => {
        // Clear any pending hide timeout
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
        
        if (index === 0) setShowGhostTop(true); // Nur bei erster Komponente
        if (index === totalElements - 1) setShowGhostBottom(true); // Nur bei letzter Komponente
    };
    
    const handleMouseLeave = (e) => {
        // ✅ Verzögertes Ausblenden - gibt Zeit zum Hovern über Ghost
        const relatedTarget = e?.relatedTarget;
        
        // ✅ FIX: Prüfe ob relatedTarget ein Element ist und .closest() verfügbar hat
        const isGoingToGhost = relatedTarget && 
                               typeof relatedTarget.closest === 'function' && 
                               relatedTarget.closest('.add-element-ghost');
        
        // Wenn Maus zum Ghost geht, nicht schließen
        if (isGoingToGhost) {
            return;
        }
        
        hideTimeoutRef.current = setTimeout(() => {
            setShowGhostTop(false);
            setShowGhostBottom(false);
        }, 150); // 150ms Delay
    };
    
    const handleGhostEnter = () => {
        // Clear hide timeout when hovering ghost
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    };
    
    const handleGhostLeave = () => {
        // ✅ Einfach: Verzögertes Schließen (Menu ist jetzt click-basiert)
        hideTimeoutRef.current = setTimeout(() => {
            setShowGhostTop(false);
            setShowGhostBottom(false);
        }, 150);
    };
    
    return (
        <div className="hero-element-outer-container">
            {/* 👻 GHOST ELEMENT - Nur ÜBER dem ERSTEN Element (NUR im Haupt-Container, NICHT in Grid-Cells) */}
            {isEditMode && !disableGhostButtons && index === 0 && showGhostTop && (
                <div 
                    className="add-element-ghost add-element-ghost-top"
                    onMouseEnter={handleGhostEnter}
                    onMouseLeave={handleGhostLeave}
                >
                    <AddElementButton 
                        onAdd={(type, elementType) => onAddBefore(type, elementType)} 
                        position="ghost" 
                    />
                </div>
            )}
            
            {/* Element Wrapper */}
            <div 
                className={`hero-content-element-wrapper ${isEditMode ? 'edit-mode' : ''}`}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={(e) => handleMouseLeave(e)}
            >
                {/* Element Content */}
                <div className="hero-content-element">
                    {element.type === 'text' && (
                        <UniversalTextElement
                            element={element}
                            isEditMode={isEditMode}
                            isMobileLayout={isMobileLayout}
                            onUpdate={onUpdate}
                            onSelect={() => onSelect && onSelect(element.id)}
                            colors={colors}
                            fonts={fonts}
                            availableFonts={availableFonts}
                            layoutConfig={layoutConfig}
                            isCurrentlyEditing={currentlyEditingElementId === element.id}
                            onEditingStart={() => onEditingChange(element.id)}
                            onEditingEnd={() => onEditingChange(null)}
                        />
                    )}
                    
                    {element.type === 'button' && (
                        <UniversalButtonElement
                            element={element}
                            isEditMode={isEditMode}
                            isMobileLayout={isMobileLayout}
                            onUpdate={onUpdate}
                            onSelect={() => onSelect && onSelect(element.id)}
                            colors={colors}
                            fonts={fonts} // ✅ FIXED: Fonts für Font Family hinzugefügt!
                            textAlignment={textAlignment}
                            isCurrentlyEditing={currentlyEditingElementId === element.id} // ✅ NEW: Global editing state
                            onEditingStart={() => onEditingChange(element.id)} // ✅ NEW: Start editing
                            onEditingEnd={() => onEditingChange(null)} // ✅ NEW: End editing
                        />
                    )}
                    
                    {element.type === 'divider' && (
                        <UniversalDividerElement
                            element={element}
                            isEditMode={isEditMode}
                            isMobileLayout={isMobileLayout}
                            onUpdate={onUpdate}
                            onSelect={() => onSelect && onSelect(element.id)}
                            colors={colors}
                            isCurrentlyEditing={currentlyEditingElementId === element.id}
                            onEditingStart={() => onEditingChange(element.id)}
                            onEditingEnd={() => onEditingChange(null)}
                        />
                    )}
                    
                    {element.type === 'image' && (
                        <UniversalImageElement
                            element={element}
                            isEditMode={isEditMode}
                            isMobileLayout={isMobileLayout}
                            onUpdate={onUpdate}
                            onSelect={() => onSelect && onSelect(element.id)}
                            colors={colors}
                            isCurrentlyEditing={currentlyEditingElementId === element.id}
                            onEditingStart={() => onEditingChange(element.id)}
                            onEditingEnd={() => onEditingChange(null)}
                        />
                    )}
                    
                    {/* 🎥 NEW: Video Element */}
                    {element.type === 'video' && (
                        <UniversalVideoElement
                            element={element}
                            isEditMode={isEditMode}
                            isMobileLayout={isMobileLayout}
                            onUpdate={onUpdate}
                            onSelect={() => onSelect && onSelect(element.id)}
                            colors={colors}
                        />
                    )}
                    
                    {/* 🎨 NEW: Gallery Element */}
                    {element.type === 'gallery' && (
                        <UniversalGalleryElement
                            element={element}
                            isEditMode={isEditMode}
                            isMobileLayout={isMobileLayout}
                            onUpdate={onUpdate}
                            colors={colors}
                            fonts={fonts} // ✅ NEW: Brand Fonts für Font Family Section
                            currentlyEditingElementId={currentlyEditingElementId}
                            onEditingChange={onEditingChange}
                        />
                    )}
                    
                    {/* 🎯 NEW: Q&A Element */}
                    {element.type === 'qa' && (
                        <UniversalQAElement
                            element={element}
                            isEditMode={isEditMode}
                            isMobileLayout={isMobileLayout}
                            onUpdate={onUpdate}
                            onSelect={() => onSelect && onSelect(element.id)}
                            colors={colors}
                            fonts={fonts}
                            availableFonts={availableFonts}
                            currentlyEditingElementId={currentlyEditingElementId}
                            onEditingChange={onEditingChange}
                        />
                    )}
                    
                    {/* 🕐 NEW: Countdown Bar Element */}
                    {element.type === 'countdownBar' && (
                        <UniversalCountdownBar
                            element={element}
                            isEditMode={isEditMode}
                            isMobileLayout={isMobileLayout}
                            onUpdate={onUpdate}
                            onSelect={() => onSelect && onSelect(element.id)}
                            colors={colors}
                            fonts={fonts}
                            isCurrentlyEditing={currentlyEditingElementId === element.id}
                            onEditingStart={() => onEditingChange(element.id)}
                            onEditingEnd={() => onEditingChange(null)}
                        />
                    )}
                    
                    {/* 👟 NEW: Footer Element */}
                    {element.type === 'footer' && (
                        <UniversalFooterElement
                            element={element}
                            isEditMode={isEditMode}
                            isMobileLayout={isMobileLayout}
                            onUpdate={onUpdate}
                            onSelect={() => onSelect && onSelect(element.id)}
                            colors={colors}
                            fonts={fonts}
                            isCurrentlyEditing={currentlyEditingElementId === element.id}
                            onEditingStart={() => onEditingChange(element.id)}
                            onEditingEnd={() => onEditingChange(null)}
                        />
                    )}
                    
                    {/* 🎨 NEW: Grid Layout Element */}
                    {element.type === 'grid' && (
                        <LayoutGridElement
                            element={element}
                            isEditMode={isEditMode}
                            isMobileLayout={isMobileLayout}
                            onUpdate={onUpdate}
                            onDelete={onDelete}
                            onDuplicate={onDuplicate} // ✅ Duplicate Grid durchreichen
                            colors={colors}
                            colorAssignments={colorAssignments}
                            fonts={fonts}
                            availableFonts={availableFonts}
                            currentlyEditingElementId={currentlyEditingElementId}
                            onEditingChange={onEditingChange}
                            depth={depth}
                            // ✅ Recursive rendering function
                            renderChild={(child, parentId) => {
                                // ✅ Helper: Create new element for cell
                                const createCellElement = (type, elementType) => {
                                    // ✅ Handle Grid Presets
                                    if (type === 'gridPreset') {
                                        const presetElement = applyGridPreset(elementType, 0, colors, fonts, colorAssignments);
                                        if (presetElement) {
                                            return presetElement;
                                        }
                                        // Fallback wenn Preset nicht existiert
                                        return null;
                                    }
                                    
                                    const baseElement = {
                                        id: `${type}-${Date.now()}`,
                                        type: type,
                                        visible: true,
                                        elementType: type === 'button' ? 'button' : elementType
                                    };
                                    
                                    if (type === 'text') {
                                        const textDefaults = {
                                            'h1': { fontSize: 48, fontSizeMobile: 36, fontWeight: '700', content: 'New Heading' },
                                            'h2': { fontSize: 36, fontSizeMobile: 28, fontWeight: '600', content: 'New Subheading' },
                                            'h3': { fontSize: 24, fontSizeMobile: 20, fontWeight: '600', content: 'New Subtitle' },
                                            'p': { fontSize: 16, fontSizeMobile: 14, fontWeight: '400', content: 'New paragraph...' }
                                        };
                                        // ✅ FIXED: Nutze Brand Font basierend auf elementType (H1 = Big Font, Rest = Small Font)
                                        const defaultFont = elementType === 'h1' 
                                            ? (fonts.globalBigFont || '') 
                                            : (fonts.globalSmallFont || '');
                                        // ✅ FIXED: Nutze Brand Text Color basierend auf colorAssignments
                                        const defaultColor = (() => {
                                            if (elementType === 'h1' || elementType === 'h2' || elementType === 'h3') {
                                                const colorKey = colorAssignments.primaryText || 'color2';
                                                return colors[colorKey] || '#333333';
                                            } else {
                                                const colorKey = colorAssignments.secondaryText || 'color3';
                                                return colors[colorKey] || '#666666';
                                            }
                                        })();
                                        return { 
                                            ...baseElement, 
                                            ...(textDefaults[elementType] || textDefaults['p']), 
                                            fontFamily: defaultFont, 
                                            color: defaultColor, 
                                            textAlign: 'center', // ✅ DEFAULT: Mittig
                                            textAlignMobile: 'center', // ✅ DEFAULT: Mittig auf Mobile
                                            marginTop: 0, 
                                            marginBottom: 0 
                                        };
                                    }
                                    
                                    if (type === 'button') {
                                        return { 
                                            ...baseElement, 
                                            buttonText: 'Click me', 
                                            buttonTextSize: 18,
                                            buttonTextSizeMobile: 16,
                                            buttonWidth: 200, // ✅ Desktop: 200px
                                            buttonWidthMobile: 100, // ✅ Mobile: 100% (Prozent-Wert)
                                            buttonMaxWidth: 300,
                                            buttonMaxWidthMobile: 280,
                                            buttonHeight: 50,
                                            buttonHeightMobile: 48,
                                            buttonBorderRadius: 8,
                                            buttonBorderRadiusMobile: 8,
                                            // ✅ FIXED: Nutze Brand Button Colors basierend auf colorAssignments
                                            buttonColor: colors[colorAssignments.buttons || 'color1'] || '#5C80BC', 
                                            buttonTextColor: colors[colorAssignments.buttonText || 'color5'] || '#FFFFFF',
                                            // ✅ FIXED: Nutze Brand Font (Small Font für Buttons)
                                            buttonFontFamily: fonts.globalSmallFont || '',
                                            buttonAlignment: 'left', 
                                            buttonAlignmentMobile: 'left', 
                                            marginTop: 0, 
                                            marginBottom: 0 
                                        };
                                    }
                                    
                                    if (type === 'image') {
                                        return { ...baseElement, imageUrl: '', width: 100, height: 300, widthType: 'percent', heightType: 'auto', borderRadius: 8, objectFit: 'cover' };
                                    }
                                    
                                    if (type === 'divider') {
                                        return { ...baseElement, dividerStyle: 'simple-line', dividerColor: colors.accentColor || '#5C80BC', dividerWidth: 100, marginTop: 20, marginBottom: 20 };
                                    }
                                    
                                    if (type === 'grid') {
                                        const cols = elementType === '2-columns' ? 2 : 3;
                                        return {
                                            ...baseElement,
                                            columns: cols,
                                            gap: 20,
                                            gapMobile: 15,
                                            columnTemplate: 'equal',
                                            marginTop: 20,
                                            marginBottom: 20,
                                            padding: 0,
                                            // ✅ Grid Direction & Flow
                                            gridDirection: 'horizontal',
                                            gridDirectionMobile: 'horizontal', // ✅ Separate Mobile Direction
                                            gridFlowMode: 'fixed',
                                            maxItemsPerRow: 3,
                                            minItemWidth: 200,
                                            // ✅ Cell Defaults (können in Cell Styling Section geändert werden)
                                            cellDefaultBackground: 'transparent',
                                            cellDefaultPadding: 12,
                                            cellDefaultPaddingMobile: 10,
                                            // ✅ Min/Max Width nicht setzen - Grid übernimmt Breite
                                            cellDefaultFlexDirection: 'column',
                                            cellDefaultFlexDirectionMobile: 'column',
                                            cellDefaultAlignItems: 'flex-start',
                                            cellDefaultJustifyContent: 'center',
                                            cellDefaultBorderRadius: 6,
                                            cellDefaultBorderEnabled: false,
                                            cellDefaultBorderWidth: 1,
                                            cellDefaultBorderColor: '#e0e0e0',
                                            cellDefaultBoxShadow: 'none', // ✅ KEIN Shadow beim Erstellen
                                            cellDefaultFixedHeight: false,
                                            cellDefaultHeight: 200,
                                            cellDefaultGap: 12,
                                            cellDefaultGapMobile: 12,
                                            // ✅ Cells ohne eigene Werte → Verwenden Grid Defaults
                                            children: Array.from({ length: cols }, (_, i) => ({
                                                id: `cell-${Date.now()}-${i}`,
                                                type: 'cell',
                                                cellIndex: i,
                                                order: i,
                                                children: []
                                            }))
                                        };
                                    }
                                    
                                    return baseElement;
                                };
                                
                                // ✅ Get parent cell and calculate proper index
                                const parentCell = element.children.find(c => c.id === parentId);
                                const cellChildren = parentCell?.children || [];
                                const childIndex = cellChildren.findIndex(cc => cc.id === child.id);
                                
                                // ✅ Determine if cell has horizontal layout (flexDirection: row)
                                const cellFlexDirection = isMobileLayout
                                    ? (element.cellDefaultFlexDirectionMobile ?? element.cellDefaultFlexDirection ?? 'column')
                                    : (element.cellDefaultFlexDirection ?? 'column');
                                const isCellHorizontal = cellFlexDirection === 'row' || cellFlexDirection === 'row-reverse';
                                
                                return (
                                    <HeroContentElement
                                        key={child.id}
                                        element={child}
                                        index={childIndex}
                                        totalElements={cellChildren.length}
                                        isEditMode={isEditMode}
                                        isMobileLayout={isMobileLayout}
                                        onUpdate={(updatedChild) => {
                                            // Update child within parent grid
                                            const updatedChildren = element.children.map(c =>
                                                c.id === parentId
                                                    ? { ...c, children: c.children.map(cc => cc.id === updatedChild.id ? updatedChild : cc) }
                                                    : c
                                            );
                                            onUpdate({ ...element, children: updatedChildren });
                                        }}
                                        onDelete={() => {
                                            // Delete child from parent grid
                                            const updatedChildren = element.children.map(c =>
                                                c.id === parentId
                                                    ? { ...c, children: c.children.filter(cc => cc.id !== child.id) }
                                                    : c
                                            );
                                            onUpdate({ ...element, children: updatedChildren });
                                        }}
                                        // ✅ FIX: Move elements within cell
                                        onMoveUp={() => {
                                            const updatedChildren = element.children.map(c => {
                                                if (c.id === parentId) {
                                                    const newChildren = [...c.children];
                                                    if (childIndex > 0) {
                                                        // Swap with previous element
                                                        [newChildren[childIndex - 1], newChildren[childIndex]] = 
                                                        [newChildren[childIndex], newChildren[childIndex - 1]];
                                                    }
                                                    return { ...c, children: newChildren };
                                                }
                                                return c;
                                            });
                                            onUpdate({ ...element, children: updatedChildren });
                                        }}
                                        onMoveDown={() => {
                                            const updatedChildren = element.children.map(c => {
                                                if (c.id === parentId) {
                                                    const newChildren = [...c.children];
                                                    if (childIndex < newChildren.length - 1) {
                                                        // Swap with next element
                                                        [newChildren[childIndex], newChildren[childIndex + 1]] = 
                                                        [newChildren[childIndex + 1], newChildren[childIndex]];
                                                    }
                                                    return { ...c, children: newChildren };
                                                }
                                                return c;
                                            });
                                            onUpdate({ ...element, children: updatedChildren });
                                        }}
                                        canMoveUp={childIndex > 0}
                                        canMoveDown={childIndex < cellChildren.length - 1}
                                        // ✅ FIX: Add element to CELL, not main container
                                        onAddBefore={(type, elementType) => {
                                            const newElement = createCellElement(type, elementType);
                                            // ✅ Only add if element was created successfully
                                            if (!newElement) return;
                                            
                                            const updatedChildren = element.children.map(c =>
                                                c.id === parentId
                                                    ? {
                                                        ...c,
                                                        children: [
                                                            newElement,
                                                            ...c.children
                                                        ]
                                                    }
                                                    : c
                                            );
                                            onUpdate({ ...element, children: updatedChildren });
                                        }}
                                        onAddAfter={(type, elementType) => {
                                            const newElement = createCellElement(type, elementType);
                                            // ✅ Only add if element was created successfully
                                            if (!newElement) return;
                                            
                                            const updatedChildren = element.children.map(c =>
                                                c.id === parentId
                                                    ? {
                                                        ...c,
                                                        children: [
                                                            ...c.children,
                                                            newElement
                                                        ]
                                                    }
                                                    : c
                                            );
                                            onUpdate({ ...element, children: updatedChildren });
                                        }}
                                        colors={colors}
                                        colorAssignments={colorAssignments}
                                        fonts={fonts}
                                        availableFonts={availableFonts}
                                        layoutConfig={layoutConfig}
                                        currentlyEditingElementId={currentlyEditingElementId}
                                        onEditingChange={onEditingChange}
                                        // ✅ Disable ghost buttons in grid cells (zu viele Plus-Buttons)
                                        disableGhostButtons={true}
                                        // ✅ Horizontal Layout für korrekte Pfeil-Richtung
                                        isHorizontalLayout={isCellHorizontal}
                                        // ✅ NEW: Depth tracking - Grid-Kinder sind eine Ebene tiefer
                                        depth={depth + 1}
                                    />
                                );
                            }}
                        />
                    )}
                </div>
                
                {/* Element Controls - Nur bis Level 2 anzeigen (depth < 2) */}
                {/* ✅ depth 0 = Top-Level, depth 1 = Grid-Cell, depth 2+ = zu tief verschachtelt */}
                {isEditMode && depth < 2 && (
                    <ElementControls
                        element={element}
                        onDelete={onDelete}
                        onMoveUp={onMoveUp}
                        onMoveDown={onMoveDown}
                        canMoveUp={canMoveUp}
                        canMoveDown={canMoveDown}
                        onSettings={() => onSelect && onSelect(element.id)}
                        isHorizontalLayout={isHorizontalLayout}
                        depth={depth}
                        currentlyEditingElementId={currentlyEditingElementId}
                        isBeingEdited={currentlyEditingElementId === element.id}
                    />
                )}
            </div>
            
            {/* 👻 GHOST ELEMENT - UNTER dem LETZTEN Element (in Grid-Cells immer aktiv, um Elemente hinzuzufügen) */}
            {isEditMode && index === totalElements - 1 && showGhostBottom && (
                <div 
                    className="add-element-ghost add-element-ghost-bottom"
                    onMouseEnter={handleGhostEnter}
                    onMouseLeave={handleGhostLeave}
                >
                    <AddElementButton 
                        onAdd={(type, elementType) => onAddAfter(type, elementType)} 
                        position="ghost" 
                    />
                </div>
            )}
        </div>
    );
}

export default HeroContentElement;

